/**
 * Récurrence métier — la couche au-dessus de la RRULE
 *
 * Une récurrence de Foyer, c'est une RRULE **plus deux champs propres au
 * domaine**, qui ne sont pas exprimables en RFC 5545 :
 *
 *  - `base` : la prochaine échéance repart-elle de la date *prévue* ou de la
 *    date *réellement réalisée* ? Le contrôle technique reste calé sur le
 *    calendrier ; un détartrage fait trois mois en retard décale toute la
 *    suite. Ce choix appartient à l'utilisateur et change tout, donc il est
 *    stocké explicitement plutôt que devinné.
 *
 *  - `ancrage` : règle relative au calendrier scolaire (« au milieu de chaque
 *    période de vacances »), qui n'a aucune régularité calendaire et remplace
 *    alors la RRULE.
 */
import { ajouteJours, enCourt, jourSemaine, joursEntre, maxDate, nomJourSemaine, parties, } from './dates.js';
import { analyseRrule, decrisRrule, genereOccurrences, occurrences, premiereOccurrenceDepuis, prochaineOccurrence, resumeRrule, } from './rrule.js';
import { LIBELLE_ANCRAGE, prochainAncrage, prochainsAncrages, } from './vacances.js';
export const RECURRENCE_PONCTUELLE = {
    rrule: null,
    depart: null,
    base: 'echeance',
    ancrage: null,
};
export function seRepete(r) {
    return !!r && (r.ancrage !== null || r.rrule !== null);
}
/**
 * Prochaine échéance d'une tâche récurrente, ou `null` si la série est finie
 * (COUNT/UNTIL atteints, ou calendrier scolaire trop court pour l'ancrage).
 */
export function prochaineEcheance(r, ctx) {
    if (!seRepete(r))
        return null;
    const reference = r.base === 'realisation' ? ctx.realiseLe : maxDate(ctx.echeance ?? ctx.realiseLe, ctx.realiseLe);
    if (r.ancrage) {
        const apres = ctx.pasAvant ? maxDate(reference, ajouteJours(ctx.pasAvant, -1)) : reference;
        return prochainAncrage(r.ancrage, apres, ctx.periodes ?? []);
    }
    const rrule = r.rrule;
    const depart = r.base === 'realisation' ? ctx.realiseLe : (r.depart ?? ctx.echeance ?? ctx.realiseLe);
    let suivante = prochaineOccurrence(depart, rrule, reference);
    if (suivante && ctx.pasAvant && suivante < ctx.pasAvant) {
        suivante = premiereOccurrenceDepuis(depart, rrule, ctx.pasAvant);
    }
    return suivante;
}
/**
 * Première échéance d'une série qu'on vient de créer, à partir d'une date
 * d'amorçage (achat, installation, dernier entretien connu).
 * `pasAvant` garantit qu'on ne programme rien dans le passé.
 */
export function premiereEcheance(r, amorce, options = {}) {
    if (r.ancrage) {
        const apres = options.pasAvant ? maxDate(amorce, ajouteJours(options.pasAvant, -1)) : amorce;
        return prochainAncrage(r.ancrage, apres, options.periodes ?? []);
    }
    if (!r.rrule)
        return amorce;
    const depart = r.depart ?? amorce;
    const brute = prochaineOccurrence(depart, r.rrule, amorce);
    if (!options.pasAvant)
        return brute;
    if (brute && brute >= options.pasAvant)
        return brute;
    return premiereOccurrenceDepuis(depart, r.rrule, options.pasAvant);
}
/** Aperçu des prochaines occurrences, affiché sous le sélecteur de récurrence. */
export function apercuOccurrences(r, depuis, nombre = 3, periodes = []) {
    if (r.ancrage)
        return prochainsAncrages(r.ancrage, ajouteJours(depuis, -1), periodes, nombre);
    if (!r.rrule)
        return [depuis];
    return occurrences(r.depart ?? depuis, r.rrule, nombre, { depuis });
}
/**
 * Toutes les occurrences d'une tâche dans un intervalle, échéance courante
 * comprise.
 *
 * Sert à l'agenda, qui affichait jusqu'ici chaque tâche **une seule fois**, à
 * son échéance du moment. Une tâche mensuelle — le paiement de la garde
 * d'enfant, par exemple — apparaissait donc au 31 août et plus jamais, alors
 * que la récurrence était bien en base. L'export iCal, lui, transportait la
 * RRULE : l'agenda de Google montrait les douze occurrences que l'agenda de
 * l'application ne montrait pas.
 *
 * L'échéance courante fait foi pour le point de départ, et non `depart` : elle a
 * pu être avancée par un cochage ou déplacée à la main. On ne projette jamais
 * avant elle — une occurrence passée n'est pas une échéance à venir.
 *
 * La borne dure existe parce qu'une règle quotidienne sur dix ans produirait des
 * milliers de dates : au-delà, ce n'est plus un agenda.
 */
const PLAFOND_OCCURRENCES = 400;
export function occurrencesEntre(r, echeance, debut, fin, periodes = []) {
    if (fin < debut || fin < echeance)
        return [];
    const dans = (d) => d >= debut && d <= fin;
    if (r.ancrage) {
        // Les dates viennent du calendrier scolaire : aucune règle à dérouler, on
        // filtre la liste connue. Elle est finie par nature.
        const suite = prochainsAncrages(r.ancrage, echeance, periodes, PLAFOND_OCCURRENCES);
        return [echeance, ...suite].filter(dans);
    }
    if (!r.rrule)
        return dans(echeance) ? [echeance] : [];
    const out = [];
    for (const d of genereOccurrences(r.depart ?? echeance, r.rrule, { depuis: echeance })) {
        if (d > fin)
            break;
        if (dans(d))
            out.push(d);
        if (out.length >= PLAFOND_OCCURRENCES)
            break;
    }
    return out;
}
/**
 * La récurrence écrite en français, affichée en direct dans le sélecteur.
 * Exemple : « Tous les 2 ans, en septembre — recalculé depuis la date de réalisation ».
 */
export function decrisRecurrence(r) {
    if (!seRepete(r))
        return 'Ne se répète pas';
    const rec = r;
    if (rec.ancrage) {
        const base = LIBELLE_ANCRAGE[rec.ancrage];
        return base.charAt(0).toUpperCase() + base.slice(1);
    }
    const texte = decrisRrule(rec.rrule);
    const suffixe = rec.base === 'realisation'
        ? ' — recalculé depuis la date de réalisation'
        : ' — calé sur le calendrier, un retard ne décale pas les suivantes';
    return texte.charAt(0).toUpperCase() + texte.slice(1) + suffixe;
}
/** Forme courte pour le badge de la carte de tâche. */
export function resumeRecurrence(r) {
    if (!seRepete(r))
        return null;
    const rec = r;
    if (rec.ancrage)
        return 'vacances scolaires';
    return resumeRrule(rec.rrule);
}
/** Valide la partie RRULE ; lève `ErreurRrule` si elle sort du sous-ensemble. */
export function valideRecurrence(r) {
    if (r.ancrage)
        return null;
    if (!r.rrule)
        return null;
    return analyseRrule(r.rrule);
}
/**
 * Dates auxquelles prévenir, pour une échéance donnée.
 *
 * Piège métier traité ici : une échéance d'assurance porte une **fenêtre de
 * résiliation** qui se ferme *avant* l'échéance. Rappeler le jour de l'échéance
 * est inutile — il est trop tard. Quand `limite` est renseignée, c'est elle qui
 * sert de référence, pas l'échéance.
 */
export function datesDeRappel(echeance, offsetsJours, limite) {
    const reference = limite ?? echeance;
    return [...new Set(offsetsJours)]
        .filter((o) => Number.isInteger(o) && o >= 0)
        .sort((a, b) => b - a)
        .map((offsetJours) => ({ date: ajouteJours(reference, -offsetJours), offsetJours }));
}
/**
 * Libellé d'échéance en langage naturel, plus le niveau d'urgence qui pilote
 * la couleur. Trois états doivent rester distinguables sans la couleur seule :
 * le texte porte l'information.
 */
export function relatif(cible, aujourdhui) {
    const n = joursDe(aujourdhui, cible);
    if (n < -1)
        return { texte: `En retard de ${-n} jours`, urgence: 'retard', jours: n };
    if (n === -1)
        return { texte: 'En retard depuis hier', urgence: 'retard', jours: n };
    if (n === 0)
        return { texte: "Aujourd'hui", urgence: 'jour', jours: n };
    if (n === 1)
        return { texte: 'Demain', urgence: 'proche', jours: n };
    if (n <= 7)
        return { texte: libelleJourProche(cible), urgence: 'proche', jours: n };
    if (n <= 31)
        return { texte: `Dans ${n} jours`, urgence: 'loin', jours: n };
    if (n <= 60)
        return { texte: 'Dans un mois', urgence: 'loin', jours: n };
    if (n <= 330)
        return { texte: `Dans ${Math.round(n / 30)} mois`, urgence: 'loin', jours: n };
    return { texte: libelleLointain(cible), urgence: 'loin', jours: n };
}
function joursDe(a, b) {
    return joursEntre(a, b);
}
function libelleJourProche(cible) {
    return `${nomJourSemaine(jourSemaine(cible))} ${parties(cible).jour}`;
}
function libelleLointain(cible) {
    return `${enCourt(cible)} ${parties(cible).annee}`;
}
//# sourceMappingURL=recurrence.js.map